import { Stack, useRouter } from 'expo-router';
import { ScrollView, XStack, YStack } from 'tamagui';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import PSSkeleton from '@/components/ui/PSSkeleton';
import { PSHeaderBackButtonOnTransparentHeader } from './PSHeaderButtons';

/**
 * BookingSkeleton — the loading placeholder for `app/bookings/[id]`.
 *
 * Shown while `useFetchBooking` is in flight (see the screen's `loading` branch).
 * It mirrors the booking page's shape closely enough that the real screen slots
 * in without a jump: the same opaque native header (blank title + back circle,
 * via the same `authHeaderLeftOptions` the page uses), the same scroll + gutter +
 * top pad, and — the key win — a header IMAGE bone at the EXACT 72×96 (3:4) box
 * `CheckoutEventHeader` renders, so the biggest fixed-size element lands in the
 * same place before and after load.
 *
 * The bones below the header (title-block lines, then a section-heading + body
 * per section) stand in for the "Booking Confirmed" heading and the Venue / Date
 * & Time / Tickets / Your details / Terms / Support sections, so the screen reads
 * as "a booking, loading" rather than a blank pulse. Their heights are the token
 * font sizes they replace (48 for the $veryLarge title, 20 for a $large heading,
 * 16 for $base body) so the vertical rhythm matches the loaded page.
 *
 * `scrollEnabled={false}` — there's nothing to scroll to yet, and it keeps the
 * placeholder inert (same as EventDetailSkeleton).
 */

// The header image is a fixed 72×96 (3:4) box in CheckoutEventHeader — the one
// dimension we can predict exactly, so the bone matches it 1:1.
const HEADER_IMAGE_W = 72;
const HEADER_IMAGE_H = 96;

export default function BookingSkeleton() {
  const router = useRouter();
  const { top: safeAreaTop } = useSafeAreaInsets();

  return (
    <YStack flex={1} bg="$color.background">
      {/* Same header as the booking page: transparent, blank title, back circle
          (wired both ways for the iOS-26 double-ring fix via authHeaderLeftOptions). */}
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,

          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        scrollEnabled={false}
        px="$lg"
        // Match the page's top pad exactly (opaque header owns the notch inset).
        pt={safeAreaTop + 50}
      >
        <PSSkeleton>
          {/* "Booking Confirmed" title block — two centered $veryLarge lines,
              same xxl bottom gap as the page. Bones are narrowed + centered so
              they read as the short two-word lines rather than full-width bars. */}
          <YStack items="center" gap="$xs" mb="$space.xxl">
            <PSSkeleton.Bone style={{ height: 48, width: '55%' }} />
            <PSSkeleton.Bone style={{ height: 48, width: '65%' }} />
          </YStack>

          <YStack gap="$space.xl">
            {/* Header row: fixed 72×96 image bone on the left, two text lines
                (title + location) stacked on the right — mirrors CheckoutEventHeader
                ($md gap, flexed column, $space.md between the two lines). */}
            <XStack gap="$md" items="flex-start">
              <PSSkeleton.Bone
                style={{ width: HEADER_IMAGE_W, height: HEADER_IMAGE_H }}
              />
              <YStack flex={1} gap="$space.md" justify="center">
                <PSSkeleton.Bone style={{ height: 20, width: '90%' }} />
                <PSSkeleton.Bone style={{ height: 20, width: '60%' }} />
              </YStack>
            </XStack>

            {/* Venue — heading + address line + map link. */}
            <YStack gap="$xs">
              <PSSkeleton.Bone style={{ height: 20, width: '30%' }} />
              <PSSkeleton.Bone style={{ height: 16, width: '60%' }} />
              <PSSkeleton.Bone style={{ height: 16, width: '45%' }} />
            </YStack>

            {/* Date & Time — heading + one line. */}
            <YStack gap="$xs">
              <PSSkeleton.Bone style={{ height: 20, width: '35%' }} />
              <PSSkeleton.Bone style={{ height: 16, width: '70%' }} />
            </YStack>

            {/* Tickets — heading + the bordered cart box. */}
            <YStack gap="$sm">
              <PSSkeleton.Bone style={{ height: 20, width: '25%' }} />
              <PSSkeleton.Bone style={{ height: 96, width: '100%' }} />
            </YStack>

            {/* Your details — heading + the bordered details box. */}
            <YStack gap="$sm">
              <PSSkeleton.Bone style={{ height: 20, width: '35%' }} />
              <PSSkeleton.Bone style={{ height: 72, width: '100%' }} />
            </YStack>

            {/* Terms — heading + a few body lines. */}
            <YStack gap="$sm">
              <PSSkeleton.Bone style={{ height: 20, width: '45%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '100%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '100%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '65%' }} />
            </YStack>

            {/* Support — heading + a short paragraph. */}
            <YStack gap="$sm">
              <PSSkeleton.Bone style={{ height: 20, width: '30%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '100%' }} />
              <PSSkeleton.Bone style={{ height: 14, width: '85%' }} />
            </YStack>
          </YStack>
        </PSSkeleton>
      </ScrollView>
    </YStack>
  );
}
